import { createTheme } from "@mui/material/styles";
import React from "react";

declare module "@mui/material/styles" {
    export interface Theme {
        palette: {
            primary: {
                main: string;
                light: string;
                dark: string;
                darker: string;
            };
            secondary: {
                main: string;
                dark: string;
                light: string;
            };
            error: {
                main: string;
                light: string;
                lighter: string;
                background: string;
            };
            warning: {
                main: string;
                dark: string;
                light: string;
                lighter: string;
            };
            success: {
                main: string;
                light: string;
            };
            info: {
                main: string;
                light: string;
                lighter: string;
            };
            text: {
                primary: string;
                secondary: string;
            };
            border: {
                main: string;
                tabs?: string;
                select?: string;
                divider?: string;
                verticalDivider?: string;
            };
            disabled: {
                thumb: string;
                track: string;
            };
            table: {
                main: string;
            };
            graphs: {
                main: string;
                dots: string;
            };
            hmi: {
                pausedBackground: string;
                pausedColor: string;
            };
            corporateBO: {
                lineSeparator: string;
                checkbox: string;
                checkboxTrack: string;
                checkboxTrackChecked: string;
                bottleneckCardBackground: string;
                buttonLineDivider: string;
                tabText: string;
            };
            hmiCodePrintPreview: {
                backgroundColor: string;
            };
            backgrounds: {
                main: string;
            };
            dropShadows: {
                main: string;
            };
            menu: {
                icons: {
                    main: string;
                };
            };
        };
        typography: {
            h1: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h2: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h3: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            bigHMIDropdown: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            subtitle1: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            subtitle2: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            error: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h5: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            h6: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
            rcaTitle: {
                fontFamily: string;
                fontSize: number;
                fontWeight: number;
            };
        };
    }
}

declare module "@mui/material/styles" {
    interface TypographyVariants {
        error: React.CSSProperties;
        bigHMIDropdown: React.CSSProperties;
        rcaTitle: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        error?: React.CSSProperties;
        bigHMIDropdown?: React.CSSProperties;
        rcaTitle: React.CSSProperties;
    }

    interface Palette {
        border: Palette["primary"];
        table: Palette["primary"];
        graphs: Palette["primary"];
        hmi: {
            pausedBackground: string;
            pausedColor: string;
        };
        corporateBO: {
            lineSeparator: string;
            checkbox: string;
            checkboxTrack: string;
            checkboxTrackChecked: string;
            bottleneckCardBackground: string;
            buttonLineDivider: string;
            tabText: string;
        };
        hmiCodePrintPreview: {
            backgroundColor: string;
        };
        disabled: {
            thumb: string;
            track: string;
        };
    }

    interface PaletteOptions {
        border:
            | PaletteOptions["primary"]
            | { tabs?: string; select?: string; divider?: string; verticalDivider?: string };
        table: PaletteOptions["primary"];
        graphs: PaletteOptions["primary"] | { dots?: string };
        hmi: {
            pausedBackground: string;
            pausedColor: string;
        };
        corporateBO: {
            lineSeparator: string;
            checkbox: string;
            checkboxTrack: string;
            checkboxTrackChecked: string;
            bottleneckCardBackground: string;
            buttonLineDivider: string;
            tabText: string;
        };
        hmiCodePrintPreview: {
            backgroundColor: string;
        };
        disabled?: {
            thumb: string;
            track: string;
        };
        backgrounds: PaletteOptions["primary"];
        dropShadows: PaletteOptions["primary"];
        menu: {
            icons: {
                main: string;
            };
        };
    }

    interface PaletteColor {
        darker?: string;
        lighter?: string;
    }

    interface SimplePaletteColorOptions {
        darker?: string;
        lighter?: string;
        background?: string;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        h1: true;
        h2: true;
        h3: true;
        bigHMIDropdown: true;

        rcaTitle: true;
        subtitle1: true;
        subtitle2: true;
        error: true;
        h5: true;
        h6: true;
    }
}

export const osornoTheme = createTheme({
    palette: {
        primary: {
            main: "#FFFFFF",
            light: "#F5FAFF",
            dark: "#8C93B1",
            darker: "#212121",
        },
        secondary: {
            main: "#EAF1FA",
            dark: "#292F37",
            light: "#F4F8FC",
        },
        error: {
            main: "#C64A4A",
            light: "#D76565",
            lighter: "#F6E4E4",
            background: "#FDD0D0",
        },
        warning: {
            main: "#EF8027",
            dark: "#F9C13D",
            light: "#F5A936",
            lighter: "#EED9A3",
        },
        success: {
            main: "#3CB371",
            light: "#4DC381",
        },
        text: {
            primary: "#192D4E",
            secondary: "#2B2E3D",
        },
        info: {
            main: "#247BA0",
            light: "#1976D2",
            lighter: "#4da0c4",
        },
        border: {
            main: "#D8D8D8",
            tabs: "#59C3C3",
            select: "#E0E5FA",
            divider: "#556482",
            verticalDivider: "#DCE1F9",
        },
        disabled: {
            thumb: "#BDBDBD",
            track: "#E0E0E0",
        },
        table: {
            main: "#F5F8FD",
        },
        graphs: {
            main: "#eeeff1",
            dots: "#D4D6E1",
        },
        hmi: {
            pausedBackground: "#ffd203",
            pausedColor: "#000000",
        },
        corporateBO: {
            lineSeparator: "#E0E5FA",
            checkbox: "#858585",
            checkboxTrack: "#D1D4E0",
            checkboxTrackChecked: "#A7CAD9",
            bottleneckCardBackground: "#FEF2E5",
            buttonLineDivider: "#74AAC1",
            tabText: "#000000",
        },
        hmiCodePrintPreview: {
            backgroundColor: "#2254b2",
        },
        menu: {
            icons: {
                main: "#2C2F41",
            },
        },
        backgrounds: {
            main: "#F9F9F9",
        },
        dropShadows: {
            main: "#133D8526",
        },
    },
    typography: {
        h1: {
            fontFamily: "Open Sans",
            fontSize: 28,
            fontWeight: 400,
        },
        h2: {
            fontFamily: "PT Mono",
            fontWeight: 400,
            fontSize: 24,
        },
        h3: {
            fontFamily: "Open Sans",
            fontSize: 20,
            fontWeight: 600,
        },
        bigHMIDropdown: {
            fontFamily: "Open Sans",
            fontSize: 22,
            fontWeight: 400,
        },
        subtitle1: {
            fontFamily: "Open Sans",
            fontSize: 16,
            fontWeight: 400,
        },
        subtitle2: {
            fontFamily: "Open Sans",
            fontSize: 14,
            fontWeight: 700,
        },
        error: {
            fontFamily: "Open Sans",
            fontSize: 14,
            fontWeight: 400,
        },
        h5: {
            fontFamily: "Open Sans",
            fontSize: 16,
            fontWeight: 700,
        },
        h6: {
            fontFamily: "Open Sans",
            fontSize: 10,
            fontWeight: 400,
        },
        rcaTitle: {
            fontFamily: "Open Sans",
            fontSize: "24px",
            fontWeight: 400,
        },
    },
    spacing: 4,
});
